import React, { useState, useCallback } from 'react';
import { Editor } from '@tiptap/react';
import {
  Bold, Italic, Link, PenTool, MoreHorizontal,
  Strikethrough, Code, Heading1, Heading2, List, ListOrdered,
  Table, ImagePlus, Undo, Redo
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface MobileEditorToolbarProps {
  editor: Editor;
  onOpenTextModal: () => void;
  onOpenImageModal: () => void;
  onOpenLinkModal: () => void;
  workbookId?: string;
  pageId?: string;
}

export function MobileEditorToolbar({
  editor,
  onOpenTextModal,
  onOpenImageModal,
  onOpenLinkModal,
  workbookId,
  pageId
}: MobileEditorToolbarProps) {
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  // Format submenu handlers
  const handleFormat = useCallback((action: string) => {
    switch (action) {
      case 'bold':
        editor.chain().focus().toggleBold().run();
        break;
      case 'italic':
        editor.chain().focus().toggleItalic().run();
        break;
      case 'strikethrough':
        editor.chain().focus().toggleStrike().run();
        break;
      case 'code':
        editor.chain().focus().toggleCode().run();
        break;
    }
    setMoreMenuOpen(false);
  }, [editor]);

  // Heading submenu handlers
  const handleHeading = useCallback((level: number) => {
    if (level === 0) {
      editor.chain().focus().setParagraph().run();
    } else {
      editor.chain().focus().toggleHeading({ level: level as 1 | 2 }).run();
    }
    setMoreMenuOpen(false);
  }, [editor]);

  // List submenu handlers
  const handleList = useCallback((type: 'bullet' | 'ordered') => {
    if (type === 'bullet') {
      editor.chain().focus().toggleBulletList().run();
    } else {
      editor.chain().focus().toggleOrderedList().run();
    }
    setMoreMenuOpen(false);
  }, [editor]);


  return (
    <div className="flex items-center justify-between p-3 bg-background/95 backdrop-blur-sm border-t">
      {/* Left side - Primary actions */}
      <div className="flex items-center gap-1">
        {/* Bold */}
        <Toggle
          size="default"
          pressed={editor.isActive('bold')}
          onPressedChange={() => editor.chain().focus().toggleBold().run()}
          aria-label="Bold"
          className="h-10 w-10 p-0"
        >
          <Bold className="h-5 w-5" />
        </Toggle>

        {/* Italic */}
        <Toggle
          size="default"
          pressed={editor.isActive('italic')}
          onPressedChange={() => editor.chain().focus().toggleItalic().run()}
          aria-label="Italic"
          className="h-10 w-10 p-0"
        >
          <Italic className="h-5 w-5" />
        </Toggle>

        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* AI Text Magic */}
        <Button
          size="default"
          onClick={onOpenTextModal}
          className="h-10 w-10 p-0 bg-gradient-to-br from-purple-600 to-violet-700 hover:from-purple-500 hover:to-violet-600 text-white"
          aria-label="AI Text Magic"
        >
          <PenTool className="h-5 w-5" />
        </Button>
      </div>

      {/* Center - Save indicator - Hidden on mobile */}
      {/* <div className="flex items-center">
        <SavingIndicator saveState={saveState} />
      </div> */}

      {/* Right side - More options */}
      <DropdownMenu open={moreMenuOpen} onOpenChange={setMoreMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            size="default"
            variant="ghost"
            className="h-10 w-10 p-0"
            aria-label="More options"
          >
            <MoreHorizontal className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {/* Formatting options */}
          <DropdownMenuItem onClick={() => handleFormat('strikethrough')}>
            <Strikethrough className="h-4 w-4 mr-2" />
            Strikethrough
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleFormat('code')}>
            <Code className="h-4 w-4 mr-2" />
            Code
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Headings */}
          <DropdownMenuItem onClick={() => handleHeading(1)}>
            <Heading1 className="h-4 w-4 mr-2" />
            Heading 1
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleHeading(2)}>
            <Heading2 className="h-4 w-4 mr-2" />
            Heading 2
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Lists */}
          <DropdownMenuItem onClick={() => handleList('bullet')}>
            <List className="h-4 w-4 mr-2" />
            Bullet List
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleList('ordered')}>
            <ListOrdered className="h-4 w-4 mr-2" />
            Numbered List
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Insert options */}
          <DropdownMenuItem onClick={onOpenLinkModal}>
            <Link className="h-4 w-4 mr-2" />
            Insert Link
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onOpenImageModal}>
            <ImagePlus className="h-4 w-4 mr-2" />
            Insert Image
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>
            <Table className="h-4 w-4 mr-2" />
            Insert Table
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Undo/Redo */}
          <DropdownMenuItem 
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          >
            <Undo className="h-4 w-4 mr-2" />
            Undo
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          >
            <Redo className="h-4 w-4 mr-2" />
            Redo
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}